import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ChevronDown, Flame, Trophy, TrendingUp, Dumbbell } from "lucide-react";

const PHONE_STYLE = {
  backgroundColor: "#0a0a0a",
  border: "6px solid #0D0D0D",
  borderRadius: "40px",
  width: "280px",
  height: "560px",
  overflow: "hidden",
};
const ACCENT = "#b4ff39";
const CARD_BG = "#141414";
const CARD_BORDER = "#222";

/* ═══ Tab 1: Log a Workout (Chest Day) ═══ */
const WorkoutTab = () => {
  const exercises = [
    { name: "Barbell Bench Press", sets: [
      { reps: 10, weight: "60kg", done: true },
      { reps: 8, weight: "80kg", done: true },
      { reps: 6, weight: "90kg", done: true },
      { reps: 5, weight: "95kg", done: false },
    ]},
    { name: "Incline Dumbbell Press", sets: [
      { reps: 12, weight: "28kg", done: true },
      { reps: 10, weight: "32kg", done: true },
      { reps: 8, weight: "34kg", done: false },
    ]},
    { name: "Cable Flyes", sets: [
      { reps: 15, weight: "14kg", done: true },
      { reps: 12, weight: "18kg", done: true },
      { reps: 12, weight: "18kg", done: false },
    ]},
    { name: "Tricep Rope Pushdown", sets: [
      { reps: 15, weight: "20kg", done: false },
      { reps: 12, weight: "25kg", done: false },
      { reps: 10, weight: "30kg", done: false },
    ]},
  ];

  const totalSets = exercises.reduce((a, e) => a + e.sets.length, 0);
  const doneSets = exercises.reduce((a, e) => a + e.sets.filter(s => s.done).length, 0);

  return (
    <div style={PHONE_STYLE} className="flex flex-col">
      <div className="p-4 flex-1 overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-1">
          <div className="text-white text-lg font-bold tracking-wide">CHEST DAY</div>
          <div className="px-2.5 py-0.5 rounded-full text-[10px] font-bold" style={{ backgroundColor: ACCENT, color: "#000" }}>
            {doneSets}/{totalSets} sets
          </div>
        </div>
        <div className="text-gray-500 text-[10px] mb-3">Monday, Apr 6</div>

        {/* Progress bar */}
        <div className="w-full h-1.5 rounded-full mb-4" style={{ backgroundColor: "#1a1a1a" }}>
          <motion.div
            className="h-full rounded-full"
            style={{ backgroundColor: ACCENT }}
            initial={{ width: 0 }}
            animate={{ width: `${(doneSets / totalSets) * 100}%` }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          />
        </div>

        {/* Exercises */}
        <div className="flex-1 space-y-2.5 overflow-hidden">
          {exercises.map((ex, ei) => {
            const exDone = ex.sets.filter(s => s.done).length;
            const allDone = exDone === ex.sets.length;
            return (
              <div key={ex.name} className="rounded-xl p-2.5" style={{ backgroundColor: CARD_BG, border: `1px solid ${CARD_BORDER}` }}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-1.5">
                    <Dumbbell size={11} style={{ color: allDone ? ACCENT : "#555" }} />
                    <span className="text-white text-[11px] font-semibold">{ex.name}</span>
                  </div>
                  <span className="text-[9px] font-medium" style={{ color: allDone ? ACCENT : "#555" }}>
                    {exDone}/{ex.sets.length}
                  </span>
                </div>
                <div className="flex gap-1.5">
                  {ex.sets.map((set, si) => (
                    <div
                      key={si}
                      className="flex-1 rounded-lg py-1 px-1 text-center"
                      style={{
                        backgroundColor: set.done ? "rgba(180,255,57,0.1)" : "#0e0e0e",
                        border: `1px solid ${set.done ? "rgba(180,255,57,0.3)" : "#1a1a1a"}`,
                      }}
                    >
                      {set.done && (
                        <Check size={8} className="mx-auto mb-0.5" style={{ color: ACCENT }} />
                      )}
                      <div className="text-[9px] font-bold" style={{ color: set.done ? ACCENT : "#666" }}>
                        {set.reps} reps
                      </div>
                      <div className="text-[8px]" style={{ color: set.done ? "rgba(180,255,57,0.6)" : "#444" }}>
                        {set.weight}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Log Set Button */}
        <div
          className="mt-3 py-2.5 rounded-xl text-center text-xs font-bold cursor-pointer"
          style={{ backgroundColor: ACCENT, color: "#000" }}
        >
          Log Next Set
        </div>
      </div>
    </div>
  );
};

/* ═══ Tab 2: Habit Tracker with Days ═══ */
const HabitTab = () => {
  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const habits = [
    {
      name: "Gym Session",
      color: "#ff3d6e",
      weeks: [
        [1,1,0,1,1,0,0],
        [1,0,1,1,1,0,0],
        [1,1,1,0,1,0,1],
        [1,1,0,1,1,0,0],
        [1,0,1,1,0,0,0],
      ],
      streak: 3,
    },
    {
      name: "Post-Dinner Walk",
      color: "#00d4ff",
      weeks: [
        [1,1,1,1,0,1,1],
        [1,1,0,1,1,1,0],
        [1,1,1,1,1,0,1],
        [0,1,1,1,1,1,1],
        [1,1,1,0,1,0,0],
      ],
      streak: 5,
    },
  ];

  const [revealed, setRevealed] = useState(0);
  const totalCells = 35;

  useEffect(() => {
    if (revealed < totalCells) {
      const timer = setTimeout(() => setRevealed(r => r + 1), 25);
      return () => clearTimeout(timer);
    }
  }, [revealed]);

  return (
    <div style={PHONE_STYLE} className="flex flex-col">
      <div className="p-4 flex-1 overflow-hidden flex flex-col">
        {/* Header */}
        <div className="text-white text-lg font-bold tracking-wide mb-0.5">HABITS</div>
        <div className="text-gray-500 text-[10px] mb-4">April 2026</div>

        {habits.map((habit) => (
          <div key={habit.name} className="mb-4">
            <div className="rounded-xl p-3" style={{ backgroundColor: CARD_BG, border: `1px solid ${CARD_BORDER}` }}>
              {/* Habit header */}
              <div className="flex items-center justify-between mb-2.5">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: habit.color, boxShadow: `0 0 8px ${habit.color}50` }} />
                  <span className="text-white text-[11px] font-semibold">{habit.name}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Flame size={10} style={{ color: habit.color }} />
                  <span className="text-[9px] font-bold" style={{ color: habit.color }}>{habit.streak} day streak</span>
                </div>
              </div>

              {/* Day labels */}
              <div className="grid grid-cols-7 gap-[3px] mb-1">
                {days.map(d => (
                  <div key={d} className="text-center text-[7px] font-semibold text-gray-600">{d}</div>
                ))}
              </div>

              {/* Heatmap grid */}
              <div className="grid grid-cols-7 gap-[3px]">
                {habit.weeks.flat().map((active, i) => (
                  <motion.div
                    key={i}
                    className="aspect-square rounded-[3px]"
                    initial={{ opacity: 0, scale: 0.3 }}
                    animate={
                      i < revealed
                        ? {
                            opacity: 1,
                            scale: 1,
                            backgroundColor: active ? habit.color : "#1a1a1a",
                            boxShadow: active ? `0 0 6px ${habit.color}40` : "none",
                          }
                        : { opacity: 0, scale: 0.3, backgroundColor: "#1a1a1a" }
                    }
                    transition={{ duration: 0.12 }}
                  />
                ))}
              </div>
            </div>
          </div>
        ))}

        {/* Legend */}
        <div className="flex items-center justify-center gap-3 mt-1">
          <div className="flex items-center gap-1.5 text-[8px] text-gray-500">
            <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: "#1a1a1a" }} /> Missed
          </div>
          <div className="flex items-center gap-1.5 text-[8px] text-gray-500">
            <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: "#ff3d6e", boxShadow: "0 0 4px #ff3d6e40" }} /> Gym
          </div>
          <div className="flex items-center gap-1.5 text-[8px] text-gray-500">
            <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: "#00d4ff", boxShadow: "0 0 4px #00d4ff40" }} /> Walk
          </div>
        </div>
      </div>
    </div>
  );
};

/* ═══ Tab 3: Strength Trend Chart ═══ */
const StrengthTrendChart = () => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setProgress((p) => (p >= 100 ? 100 : p + 1.5));
    }, 16);
    return () => clearInterval(timer);
  }, []);

  const dataPoints = [
    { week: "W1", value: 60 },
    { week: "W2", value: 72 },
    { week: "W3", value: 68 },
    { week: "W4", value: 85 },
    { week: "W5", value: 90 },
    { week: "W6", value: 95 },
    { week: "W7", value: 105 },
    { week: "W8", value: 115 },
  ];

  const maxVal = 130;
  const chartW = 280;
  const chartH = 160;
  const padY = 10;

  const points = dataPoints.map((d, i) => ({
    x: (i / (dataPoints.length - 1)) * chartW,
    y: padY + (1 - d.value / maxVal) * (chartH - padY * 2),
  }));

  const visibleCount = Math.ceil((Math.min(progress, 100) / 100) * points.length);
  const visiblePoints = points.slice(0, visibleCount);
  const pathD = visiblePoints.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
  const areaD = visiblePoints.length > 1
    ? `${pathD} L ${visiblePoints[visiblePoints.length - 1].x} ${chartH} L ${visiblePoints[0].x} ${chartH} Z`
    : "";

  return (
    <div style={PHONE_STYLE} className="flex flex-col">
      <div className="p-4 h-full flex flex-col">
        <div className="text-white text-lg font-bold tracking-wide mb-0.5">PROGRESS</div>
        <div className="text-gray-500 text-[10px] mb-3">Mon, Apr 6</div>

        <div className="flex gap-2 mb-3">
          {["7d", "30d", "90d", "All"].map((label, i) => (
            <div key={label} className="px-3 py-1 rounded-full text-[10px] font-medium"
              style={{ backgroundColor: i === 1 ? ACCENT : "#1a1a1a", color: i === 1 ? "#000" : "#666" }}>
              {label}
            </div>
          ))}
        </div>

        <div className="rounded-xl p-3 mb-3" style={{ backgroundColor: CARD_BG, border: `1px solid ${CARD_BORDER}` }}>
          <div className="text-gray-500 text-[9px] tracking-widest uppercase mb-2">Strength Trend</div>
          <div className="flex gap-4">
            {[{ val: "140kg", label: "Best Squat" }, { val: "8", label: "Exercises" }, { val: "+12%", label: "Progress" }].map(s => (
              <div key={s.label}>
                <div className="text-base font-bold" style={{ color: ACCENT }}>{s.val}</div>
                <div className="text-[8px] text-gray-500 uppercase">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-lg px-3 py-2 mb-3 flex items-center justify-between" style={{ backgroundColor: "#1a1a1a", border: "1px solid #333" }}>
          <span className="text-white text-xs font-medium">Barbell Squat</span>
          <ChevronDown size={12} className="text-gray-600" />
        </div>

        <div className="flex-1 relative">
          <svg viewBox={`0 0 ${chartW} ${chartH}`} className="w-full h-full" preserveAspectRatio="xMidYMid meet">
            <defs>
              <linearGradient id="chartGlow" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={ACCENT} stopOpacity="0.3" />
                <stop offset="100%" stopColor={ACCENT} stopOpacity="0" />
              </linearGradient>
            </defs>
            {[0.25, 0.5, 0.75].map(f => (
              <line key={f} x1="0" y1={chartH * f} x2={chartW} y2={chartH * f} stroke="#1a1a1a" strokeWidth="0.5" />
            ))}
            {visiblePoints.length > 1 && <path d={areaD} fill="url(#chartGlow)" />}
            {visiblePoints.length > 1 && (
              <path d={pathD} fill="none" stroke={ACCENT} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            )}
            {visiblePoints.map((p, i) => (
              <g key={i}>
                <circle cx={p.x} cy={p.y} r="5" fill={ACCENT} fillOpacity="0.15" />
                <circle cx={p.x} cy={p.y} r="3" fill={ACCENT} />
              </g>
            ))}
          </svg>
        </div>

        <div className="flex justify-between mt-1 px-1">
          {dataPoints.map(d => <span key={d.week} className="text-[8px] text-gray-600">{d.week}</span>)}
        </div>
      </div>
    </div>
  );
};

/* ═══ Tab 4: Personal Records ═══ */
const PersonalRecordsTab = () => {
  const records = [
    { exercise: "Barbell Squat", pr: "140 kg", date: "Mar 28", icon: "squat", improvement: "+5kg" },
    { exercise: "Bench Press", pr: "100 kg", date: "Apr 2", icon: "bench", improvement: "+2.5kg" },
    { exercise: "Deadlift", pr: "180 kg", date: "Mar 15", icon: "dead", improvement: "+10kg" },
    { exercise: "Overhead Press", pr: "65 kg", date: "Apr 5", icon: "ohp", improvement: "+2.5kg" },
    { exercise: "Barbell Row", pr: "95 kg", date: "Mar 20", icon: "row", improvement: "+5kg" },
  ];

  return (
    <div style={PHONE_STYLE} className="flex flex-col">
      <div className="p-4 flex-1 overflow-hidden flex flex-col">
        <div className="flex items-center justify-between mb-0.5">
          <div className="text-white text-lg font-bold tracking-wide">RECORDS</div>
          <div className="flex items-center gap-1">
            <Trophy size={12} style={{ color: ACCENT }} />
            <span className="text-[10px] font-bold" style={{ color: ACCENT }}>5 PRs</span>
          </div>
        </div>
        <div className="text-gray-500 text-[10px] mb-4">All-time personal bests</div>

        {/* Total volume card */}
        <div className="rounded-xl p-3 mb-3" style={{ backgroundColor: CARD_BG, border: `1px solid ${CARD_BORDER}` }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: "rgba(180,255,57,0.1)" }}>
              <TrendingUp size={18} style={{ color: ACCENT }} />
            </div>
            <div>
              <div className="text-white text-sm font-bold">580 kg</div>
              <div className="text-[9px] text-gray-500">Combined Big 3 Total (S/B/D)</div>
            </div>
          </div>
        </div>

        {/* PR list */}
        <div className="flex-1 space-y-2 overflow-hidden">
          {records.map((rec, i) => (
            <motion.div
              key={rec.exercise}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: i * 0.08 }}
              className="rounded-xl p-2.5 flex items-center justify-between"
              style={{ backgroundColor: CARD_BG, border: `1px solid ${CARD_BORDER}` }}
            >
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: "#1a1a1a" }}>
                  <Dumbbell size={14} style={{ color: ACCENT }} />
                </div>
                <div>
                  <div className="text-white text-[11px] font-semibold">{rec.exercise}</div>
                  <div className="text-[9px] text-gray-500">{rec.date}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm font-bold" style={{ color: ACCENT }}>{rec.pr}</div>
                <div className="text-[8px] font-medium" style={{ color: "#4ade80" }}>{rec.improvement}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ═══ Tabs Container ═══ */
const tabs = [
  { id: "workout", label: "Log a workout", Component: WorkoutTab },
  { id: "habits", label: "Track habits", Component: HabitTab },
  { id: "progress", label: "View progress", Component: StrengthTrendChart },
  { id: "records", label: "Personal Records", Component: PersonalRecordsTab },
];

const InteractiveDemo = () => {
  const [activeTab, setActiveTab] = useState("workout");
  const ActiveComponent = tabs.find(t => t.id === activeTab)?.Component;

  return (
    <section
      data-testid="interactive-demo-section"
      className="bg-[#F5F5F5] py-20 sm:py-28 lg:py-32"
    >
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.5 }}
          className="text-center mb-10"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-[#0D0D0D] tracking-tight mb-3">
            See it in action
          </h2>
        </motion.div>

        <div className="flex justify-center gap-1 flex-wrap mb-8" data-testid="demo-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              data-testid={`demo-tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`relative px-4 sm:px-5 py-2.5 text-sm font-medium transition-colors ${
                activeTab === tab.id ? "text-[#0D0D0D]" : "text-[#999] hover:text-[#666]"
              }`}
            >
              {tab.label}
              {activeTab === tab.id && (
                <motion.div
                  layoutId="activeTab"
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#0D0D0D]"
                  transition={{ type: "spring", stiffness: 400, damping: 30 }}
                />
              )}
            </button>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.5 }}
          className="flex justify-center"
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.3 }}
              style={{ filter: "drop-shadow(0 25px 60px rgba(0,0,0,0.15))" }}
            >
              {ActiveComponent && <ActiveComponent />}
            </motion.div>
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};

export default InteractiveDemo;
